import React, { useRef } from 'react';
import Button from '@mui/material/Button';
import ValidationErrorLabel from '../ValidationErrorLabel/ValidationErrorLabel';
import style from './FileUploadField.module.scss';
import fileIcon from '../../../Assets/images/file.svg';
import closeIcon from '../../../Assets/images/close.svg';
import upload from '../../../Assets/images/bootstrap-upload.png';
import colors from '../../../scss/colors';
import { CV_FILE_ALLOWED_FORMATS, MAX_CV_FILE_SIZE } from '../../../utils/constants';
import MainButton from '../buttons/MainButton';
import { buttonVariants } from '../buttons/types';
import { CircularProgress } from '@mui/material';

const getFileExtension = (fileName) => fileName?.toLowerCase().split('.').pop();

const validateFile = (file) => {
  const errors = [];
  const ext = getFileExtension(file.name);
  if (!CV_FILE_ALLOWED_FORMATS.includes(ext)) errors.push(`${file.name} has an unsupported file type.`);
  if (file.size > MAX_CV_FILE_SIZE) errors.push(`${file.name} exceeds the maximum file size limit.`);
  return errors;
};

const validateFiles = (files) => files.flatMap(validateFile);

const FileUploadField = ({
  label,
  file,
  setFile,
  fileName,
  setFileName,
  fileList,
  setFileList,
  errors = [],
  setErrors,
  isOptional = false,
  multiple = false,
  maxFiles = 3,
  error = false,
  disabled = false,
  showReplace = false,
  showClose = true,
  cvReq = false,
  validationMessage,
  customClass = '',
  customButtonClass = '',
  uploadText = 'Drop files to upload or',
  onFileChange = () => {},
  uploadHighlightText = 'browse your files',
  isUploading = false,
}) => {
  const fileInputRef = useRef(null);
  const handleFileChange = (e) => {
    const files = e.target.files || e.dataTransfer?.files;
    if (!files || !files.length) return;

    if (multiple) {
      const newFiles = Array.from(files);
      const combined = [...(fileList || []), ...newFiles];
      if (combined.length > maxFiles) {
        setErrors([`You can only upload up to ${maxFiles} files.`]);
        return;
      }
      const errs = validateFiles(newFiles);
      if (errs.length) {
        setErrors(errs);
        return;
      }
      setFileList(combined);
      setErrors([]);
    } else {
      const file = files[0];
      const errs = validateFile(file);
      if (errs.length) {
        setErrors(errs);
        return;
      }
      setFile(file);
      setFileName?.(file.name);
      setErrors([]);
    }

    onFileChange?.(e);
  };

  const removeFile = (index) => {
    if (!fileList) return;
    const updated = fileList.filter((_, i) => i !== index);
    setFileList(updated);
  };

  return (
    <div className={`${style.uploadWrapper} ${customClass}`} onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFileChange(e);
        }}
    >
      {label && ( <label className={`${style.input_label} ${error ? style.input_label_error : ''}`}>
        {label} {isOptional && <span className={style.optional_text}>(Optional)</span>}
      </label> )}
      {(file || fileName) && !multiple ? (
        <div className={style.uploadContainer}>
          <div className={style.uploadedFile}>
            <div className={style.uploadedFileName}>
              <img alt="file" className={style.file_icon} src={fileIcon} />
              <span className={style.file_name}>{file?.name || fileName}</span>
            </div>
            {showClose && ( <img onClick={() => setFile(null)} alt="close" className={style.closeIcon} src={closeIcon} /> )}
          </div>
        </div>
      ) : fileList?.length > 0 ? (
        <div className={style.uploadContainer}>
          {fileList.map((f, i) => (
            <div key={i} className={style.uploadedFile}>
              <div className={style.uploadedFileName}>
                <img alt="file" className={style.file_icon} src={fileIcon} />
                <span className={style.file_name}>{f.name}</span>
              </div>
              <img onClick={() => removeFile(i)} alt="close" className={style.closeIcon} src={closeIcon} />
            </div>
          ))}
        </div>
      ) : (
        <label className={style.uploadContainerLabel} style={{ cursor: 'pointer' }}>
          <img className={style.uploadeImage} src={upload} alt="upload" />
          <div className={style.uploadText}>
            {uploadText} <span className={style.browseFile}>{uploadHighlightText}</span>
          </div>
          <input
            type="file"
            accept=".pdf,.docx,.doc"
            onChange={handleFileChange}
            disabled={disabled}
            multiple={multiple}
            hidden
          />
        </label>
      )}
      {cvReq && validationMessage?.CV_REQUIRED && <ValidationErrorLabel message={validationMessage.CV_REQUIRED} />}
      {!!errors.length && errors.map((err, i) => <ValidationErrorLabel key={i} message={err} />)}
      {showReplace && (file || fileName) && (
        <>
          <input
            type="file"
            accept=".pdf,.docx,.doc"
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          <MainButton
            outlined
            rounded
            variant={buttonVariants.SECONDARY}
            action={() => fileInputRef.current?.click()}
          >
            {isUploading ? (
              <CircularProgress
                size={24}
              />
            ) : (
              'Replace CV / Resume'
            )}
          </MainButton>

        </>
      )}
    </div>
  );
};

export default FileUploadField;
